import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResponse } from '@core/interface/api-response.interface';
import { PageableResponse } from '@core/interface/pageable.interface';
import { UrlBuilder } from '@core/model/url-builder';
import { ConfirmPurchaseFileUploadRequestDto } from '@domain/purchase-file/dtos/confirm-purchase-file-upload.request.dto';
import { CreatePurchaseFileUploadSasRequestDto } from '@domain/purchase-file/dtos/create-purchase-file-upload-sas.request.dto';
import { ListPurchaseFileRequestDto } from '@domain/purchase-file/dtos/list-purchase-file.request.dto';
import { PurchaseFileUploadSasResponseDto } from '@domain/purchase-file/dtos/purchase-file-upload-sas.response.dto';
import { PurchaseFileResponseDto } from '@domain/purchase-file/dtos/purchase-file.response.dto';
import { PURCHASE_FILE_FALLBACK_CONTENT_TYPE } from '@shared/utils/constants/purchase-file.constant';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PurchaseFileService extends UrlBuilder {
  /**
   * Client built straight on `HttpBackend`, which is the head of the HTTP chain
   * *before* any interceptor runs. It exists for one call only: the PUT that
   * uploads the reconciliation file directly to Azure Blob Storage.
   *
   * That request must not go through the app interceptors because:
   * - `AuthInterceptor` sets `Authorization` and `X-Organization-Company` on
   *   every request, and Azure rejects the SAS signature when unexpected
   *   headers arrive. It would also overwrite `Content-Type` with JSON.
   * - `refreshInterceptor` would treat a 401 from Azure as an expired app token
   *   and kick off the refresh flow (logging the user out on failure).
   * - `errorInterceptor` would raise its generic toast on a 403, hiding the
   *   real cause (an expired or malformed SAS) behind "Acceso Denegado".
   *
   * Bypassing at the client level rather than allow-listing the URL inside the
   * three interceptors keeps the exception in one place and avoids matching
   * against storage-account hostnames that differ per environment.
   */
  private readonly blobStorageHttpClient: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
  ) {
    super('purchase/file');
    this.blobStorageHttpClient = new HttpClient(httpBackend);
  }

  createUploadSas(request: CreatePurchaseFileUploadSasRequestDto) {
    const queryUrl = `${this.getApiUrl()}/upload/sas`;
    return this.http.post<ApiResponse<PurchaseFileUploadSasResponseDto>>(
      queryUrl,
      request,
    );
  }

  uploadToBlobStorage(
    uploadUrl: string,
    file: File,
    requiredHeaders: Record<string, string>,
  ): Observable<HttpEvent<void>> {
    return this.blobStorageHttpClient.put<void>(uploadUrl, file, {
      headers: new HttpHeaders({
        ...requiredHeaders,
        'Content-Type': file.type || PURCHASE_FILE_FALLBACK_CONTENT_TYPE,
      }),
      reportProgress: true,
      observe: 'events',
    });
  }

  confirmUpload(request: ConfirmPurchaseFileUploadRequestDto) {
    const queryUrl = `${this.getApiUrl()}/upload/${request.purchaseFileRefId}/confirm`;
    return this.http.post<ApiResponse<PurchaseFileResponseDto>>(queryUrl, {
      originalName: request.originalName,
      sizeBytes: request.sizeBytes,
    });
  }

  list(request: ListPurchaseFileRequestDto) {
    const queryUrl = `${this.getApiUrl()}/list`;
    return this.http.get<
      ApiResponse<PageableResponse<PurchaseFileResponseDto>>
    >(queryUrl, { params: { ...request } });
  }

  reconcile(id: string) {
    // El endpoint se llama "reprocess" por historia, pero lo que hace es
    // re-publicar la solicitud de CONCILIACION del batch (no re-parsea el
    // archivo): el reconciler solo evalua las operaciones UNRECONCILED.
    const queryUrl = `${this.getApiUrl()}/reprocess/${id}`;
    return this.http.post<ApiResponse<PurchaseFileResponseDto>>(queryUrl, {});
  }

  downloadErrors(id: string) {
    const queryUrl = `${this.getApiUrl()}/download/${id}/errors`;
    return this.http.get<ApiResponse<{ viewUrl: string }>>(queryUrl);
  }

  downloadFile(id: string) {
    const queryUrl = `${this.getApiUrl()}/download/${id}`;
    return this.http.get<ApiResponse<{ viewUrl: string }>>(queryUrl);
  }
}
